import React, { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import classNames from 'classnames';
import UploadProgressIndicator from './upload-progress-indicator';
import Thumbnail from '../chat/chat-document-thumbnail';
import type { MediaDownloadLink, MediaItem } from '../../stores/types/media';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { DOWNLOAD_TYPES } from '../../types/constants';
import LoadingSpinner from '../general-components/loading-spinner';
import ISORow from './iso-row';
import colors from '../../utils/colors';
import { resolveIsoStatus } from '../../utils/iso-status';
import { updateSingleItem } from '../../socket-listeners/media';

interface MyProps {
	downloadMedia: (
		media: MediaItem,
		downloadType: string,
		preCallback?: Function,
		postCallback?: Function,
		mediaDownloadLink?: MediaDownloadLink,
	) => Promise<void>;
	ellipses: any;
	isCurrentlyUploading: boolean,
	mediaItem: MediaItem,
	mediaThumbnailURL: string,
	progress: number | undefined,
	thisMediaIsDownloading: boolean,
	isHost: boolean,
	userCanControl: boolean,
}

export default function ExpandedMediaLink(props: MyProps) {
	const {
		downloadMedia,
		ellipses,
		isCurrentlyUploading,
		mediaItem,
		mediaThumbnailURL,
		progress,
		thisMediaIsDownloading,
		isHost,
		userCanControl,
	} = props;

	const [isDownloadingISO, setIsDownloadingISO] = useState<number | undefined>(undefined);

	const isoDownloadReady = useMemo(() => {
		if (!mediaItem.download_links || !mediaItem.iso_url) {
			return false;
		}

		if (
			mediaItem.iso_url?.length === 1
			&& mediaItem.iso_url[0]?.includes('.zip')
		) {
			return false;
		} if (
			mediaItem?.number_of_isos !== undefined && mediaItem?.number_of_isos
			&& mediaItem.number_of_isos + 1 > mediaItem.iso_url?.length
		) {
			return false;
		}
		return true;
	}, [mediaItem?.number_of_isos, mediaItem.iso_url, mediaItem.download_links]);

	const isoDownloads = useMemo(() => (
		mediaItem.download_links
			?.filter((m: MediaDownloadLink) => m.type === 'iso')
			?.sort((a, b) => (a?.label ?? '')?.localeCompare(b?.label ?? ''))
	), [mediaItem.download_links]);

	const handleDownload = async (
		// eslint-disable-next-line @typescript-eslint/no-shadow
		mediaItem: MediaItem,
		index: number,
		mediaDownloadLink?: MediaDownloadLink,
	): Promise<void> => {
		setIsDownloadingISO(index);

		await downloadMedia(
			mediaItem,
			DOWNLOAD_TYPES.SINGLE_ISO,
			undefined,
			setIsDownloadingISO,
			mediaDownloadLink,
		);
	};

	const getSkeletonsForIsosLeft = useMemo(() => {
		let numberOfIsosLeft = !mediaItem?.number_of_isos || Number(mediaItem?.number_of_isos) === 0
			? 3
			: mediaItem.number_of_isos + 1 - (mediaItem.iso_url?.length ?? 0);

		if (numberOfIsosLeft <= 0) {
			numberOfIsosLeft = 3;
		} else if (numberOfIsosLeft >= 5) {
			numberOfIsosLeft = 5;
		}

		const skeletons = [];
		for (let i = 0; i < numberOfIsosLeft; i += 1) {
			skeletons.push((
				<div className="iso-row" key={`iso-skeleton-row-${i}`}>
					<div className="iso-column-1">
						<Skeleton />
					</div>
					<div className="iso-column-2">
						<Skeleton />
					</div>
				</div>
			));
		}
		return skeletons;
	}, [mediaItem.number_of_isos, mediaItem.iso_url]);

	const hasFeeds = (isoDownloads?.length ?? 0) > 0;

	// Resolve the effective ISO status.
	const isoStatus = useMemo(() => resolveIsoStatus({
		isoStatus: mediaItem.iso_status,
		isoDownloadReady,
		hasFeeds,
		createdDate: mediaItem.created_date,
	}), [mediaItem.iso_status, isoDownloadReady, hasFeeds, mediaItem.created_date]);

	// A status promising feeds shows the skeleton until the rows themselves arrive.
	const showSkeleton = isoStatus === 'processing'
		|| ((isoStatus === 'complete' || isoStatus === 'partial') && !hasFeeds);

	// Only the single-item endpoint returns ISO download links, so a list refresh leaves an
	// expanded recording without the rows its status promises. Fetch this item directly
	// whenever we land in that state, and again if we land back in it after recovering.
	const needsFeeds = !hasFeeds && (isoStatus === 'complete' || isoStatus === 'partial');
	const requestedFeedsFor = useRef<number | null>(null);

	useEffect(() => {
		if (!needsFeeds) {
			requestedFeedsFor.current = null;
			return;
		}

		// One request per stretch of missing rows — the state persists until they arrive.
		if (requestedFeedsFor.current === mediaItem.id) {
			return;
		}

		requestedFeedsFor.current = mediaItem.id;
		updateSingleItem({ message: { id: mediaItem.id } });
	}, [needsFeeds, mediaItem.id]);

	return (
		<div className="expanded-media-item">
			<div className="expanded-media-contents">
				<div className="canvas-recording">
					<div className="thumbnail">
						{(isCurrentlyUploading || !mediaThumbnailURL)
							? (
								<UploadProgressIndicator progress={progress} />
							) : (
								<div
									className={classNames('thumbnail-container', mediaItem?.media_data.fill && 'fill')}
									style={{ background: mediaItem?.media_data.background_color ?? colors.darkerGray }}
								>
									<Thumbnail thumbnail={mediaThumbnailURL} />
								</div>
							)}
					</div>
					<div className="canvas-label">
						Canvas Recording
					</div>
					<div className='canvas-ellipses'>
						{thisMediaIsDownloading && (isHost || userCanControl) && (
							<div className="media-download-spinner">
								<LoadingSpinner height={24} width={24} color={colors.textGray} stroke={10} />
							</div>
						)}
						{!thisMediaIsDownloading && ellipses}
					</div>
				</div>

				<div className='expanded-media-header'>
					Feeds
				</div>

				{isoStatus === 'failed' ? (
					<div className="iso-downloads-failed" data-testid="iso-failed">
						<Icon name={ICONS.ERROR} color={COLORS.SEMANTIC_RED_400} size={14} />
						<span>ISO recordings could not be generated.</span>
					</div>
				) : isoStatus === 'none' ? (
					<div className="iso-downloads-none" data-testid="iso-none">
						<Icon name={ICONS.INFO_CIRCLE} color={COLORS.TEXT_GRAY_400} size={13} />
						<span>No ISO recordings were generated for the recordings.</span>
					</div>
				) : (
					<>
						{(isoDownloads?.length ?? 0) > 0 && (
							<div className="iso-downloads">
								{isoDownloads?.map((download: MediaDownloadLink, index: number) => (
									<ISORow
										key={`${index}-${mediaItem?.download_name}`}
										index={index}
										mediaItem={mediaItem}
										canDownload={isHost || userCanControl}
										isDownloading={isDownloadingISO === index}
										download={download}
										downloadCallback={handleDownload}
									/>
								))}
							</div>
						)}

						{isoStatus === 'partial' && (
							<div className="iso-downloads-note" data-testid="iso-partial-note">
								<Icon name={ICONS.INFO_CIRCLE} color={COLORS.TEXT_GRAY_400} size={13} />
								<span>Some isolated recordings are unavailable.</span>
							</div>
						)}

						{showSkeleton && (
							<div className="iso-downloads-skeleton">
								{getSkeletonsForIsosLeft}
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
